import { error } from '@sveltejs/kit';

import { seoConfig } from '@shared/configs';
import { extendBreadcrumbsWithSlug } from '@shared/ui/breadcrumbs';
import { parseSlug } from '@shared/utils';

import { ArtAssetCardApi } from '@entities/art-asset';
import { CuratorApi, mapDataToIU } from '@entities/curator';
import { slugLibs } from '@entities/slug';

import { ARTWORKS_PER_PAGE } from '@pages/artworks';

import type { PageLoad } from './$types';

export const ssr = true;

export const load: PageLoad = async ({ fetch, data, params }) => {
  const {
    id,
    artworksLoadMore,
    artworksSortColumn,
    artworksSortOrder,
    searchParams,
    page,
    extraPath,
    filters,
    configurationsId,
  } = data;
  const artworkApi = new ArtAssetCardApi(fetch);
  const curatorApi = new CuratorApi(fetch);

  const parsedParams = parseSlug(searchParams);

  const [curator, { artworks, totalCount: artworksCount }] = await Promise.all([
    curatorApi.readOne(id).then((response) => mapDataToIU(response)),
    artworkApi.readMany({
      ...parsedParams,
      page: page,
      loadMore: artworksLoadMore,
      perPage: ARTWORKS_PER_PAGE,
      offset: (page - 1) * ARTWORKS_PER_PAGE,
      institutionId: Number(configurationsId),
      sort: artworksSortColumn,
      order: artworksSortOrder,
      curators: id,
    }),
  ]);

  if (!curator.id) {
    throw error(404, { message: 'Curator not found' });
  }
  slugLibs.handleSlugMismatch({
    currentSlug: params.slug,
    entitySlug: curator.slug,
    redirectPath: `/curator-${curator.id}-${curator.slug}/`,
  });

  const baseBreadcrumbs = [
    {
      name: 'Home',
      slug: '/',
      canonical: false,
    },
    {
      name: 'Curators',
      slug: '/curators/',
      canonical: false,
    },
    {
      name: curator.firstName + ' ' + curator.lastName,
      slug: `/curator-${curator.id}-${curator.slug}/`,
      canonical: !extraPath,
    },
  ];

  const breadcrumbs = extendBreadcrumbsWithSlug({
    baseBreadcrumbs,
    name: 'Artworks filters options',
    slug: extraPath,
  });

  const seo = seoConfig.curator({
    curatorName: curator.lastName + ' ' + curator.firstName,
    breadcrumbs,
    robots: !filters,
  });

  return {
    curator,
    artworks,
    artworksCount,
    artworksPage: page,
    artworksLoadMore,
    seo,
    hasParams: !!searchParams,
  };
};
